import * as React from 'react';

import {Modal} from 'shells/modal/modal';

import type {
    EditionFragment as DripEdition,
    SmsDripEditionFragment as SmsDripEdition,
    DripSequenceFragment as DripSequence,
    SmsDripSequenceFragment as SmsDripSequence,
} from 'nutshell-graphql-types';

import {getEditionsMissingDelays, performRequiredSequenceChecks} from './helpers';
import {PrepareToActivate} from './prepare-to-activate';
import {ReviewMessages} from './review-messages';
import {ReviewSequence} from './review-sequence';

import styles from './activate-sequence-modal.module.css';

type Props = {
    isOpen: boolean;
    dripSequence: DripSequence | SmsDripSequence;
    editions: DripEdition[] | SmsDripEdition[];
    streetAddress?: string | null | undefined;
    canAccessSetup: boolean;
    isLoading: boolean;
    onCloseModal: () => void;
    onConfirmActivate: (dripSequenceId: string) => Promise<any>;
    scrollToEdition: (arg1: string) => void;
};

type ModalState = 'review-sequence' | 'review-messages' | 'prepare-to-activate';

export function ActivateDripSequenceModal(props: Props) {
    // State to control the content being shown in the modal
    const [modalState, setModalState] = React.useState<ModalState>('review-sequence');

    // Modal needs to be wide for reviewing messages
    const isWide = modalState === 'review-messages';

    const editionsMissingDelays = getEditionsMissingDelays(props.editions);
    const {hasSequenceErrors, shouldDisplayReviewSequence} = performRequiredSequenceChecks(
        props.dripSequence,
        editionsMissingDelays
    );

    React.useEffect(() => {
        if (modalState === 'review-sequence' && !shouldDisplayReviewSequence) {
            setModalState('review-messages');
        }
    }, [modalState, shouldDisplayReviewSequence]);

    let modalContent;
    switch (modalState) {
        case 'review-sequence':
            modalContent = (
                <ReviewSequence
                    onClickPrimary={() => setModalState('review-messages')}
                    onClickSecondary={props.onCloseModal}
                    dripSequence={props.dripSequence}
                    isReviewDisabled={hasSequenceErrors}
                    closeModal={props.onCloseModal}
                    scrollToEdition={props.scrollToEdition}
                    editionsMissingDelays={editionsMissingDelays}
                    canAccessSetup={props.canAccessSetup}
                />
            );
            break;
        case 'review-messages':
            modalContent = (
                <ReviewMessages
                    onClickPrimary={() => setModalState('prepare-to-activate')}
                    onClickSecondary={props.onCloseModal}
                    // @ts-expect-error
                    closeModal={props.onCloseModal}
                    scrollToEdition={props.scrollToEdition}
                    isLoading={props.isLoading}
                    editions={props.editions}
                    streetAddress={props.streetAddress}
                    canAccessSetup={props.canAccessSetup}
                />
            );
            break;
        case 'prepare-to-activate':
            modalContent = (
                <PrepareToActivate
                    onClickPrimary={props.onConfirmActivate}
                    onClickSecondary={() => setModalState('review-messages')}
                    dripSequence={props.dripSequence}
                />
            );
            break;
    }

    return (
        <Modal
            isOpen={props.isOpen}
            // @ts-expect-error
            maxHeight='600px'
            noPadding={true}
            isWide={isWide}
            onClose={props.onCloseModal}
            canClickOutsideToClose={false}
        >
            <div className={styles['activate-sequence-modal']}>{modalContent}</div>
        </Modal>
    );
}
